import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  Drawer,
  EmptyState,
  Input,
  Select,
  Switch,
  useToast,
} from '../../../ui/components';
import { IconButton } from '../../../ui/components/Button';
import { Icon } from '../../../ui/icons';
import { useT } from '../../../i18n';
import { useCommercialSettings } from '../../../app/PropertyContext';
import { suppliersApi } from '../suppliersApi';

const EMPTY = {
  label: '', bankName: '', branch: '', accountName: '', accountNumber: '',
  currency: '', swiftCode: '', iban: '', isPrimary: false, notes: '',
};

const normalize = (b, defaultCurrency) => (b
  ? {
    label: b.label ?? '', bankName: b.bankName ?? '', branch: b.branch ?? '',
    accountName: b.accountName ?? '', accountNumber: b.accountNumber ?? '',
    currency: b.currency ?? defaultCurrency, swiftCode: b.swiftCode ?? '', iban: b.iban ?? '',
    isPrimary: !!b.isPrimary, notes: b.notes ?? '',
  }
  : { ...EMPTY, currency: defaultCurrency });

function BankAccountForm({ initial, submitting, onSubmit, onCancel }) {
  const t = useT();
  const { currencies, defaultCurrency } = useCommercialSettings();
  const [form, setForm] = useState(() => normalize(initial, defaultCurrency));
  const [errors, setErrors] = useState({});
  useEffect(() => { setForm(normalize(initial, defaultCurrency)); setErrors({}); }, [initial, defaultCurrency]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Currencies configured in Settings; keep an existing account's currency visible
  // even if it's outside the configured set.
  const currencyOptions = useMemo(() => {
    const codes = [...currencies];
    if (form.currency && !codes.includes(form.currency)) codes.push(form.currency);
    return codes.map((c) => ({ value: c, label: c }));
  }, [currencies, form.currency]);

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.bankName.trim()) next.bankName = t('Bank name is required.');
    if (!form.accountName.trim()) next.accountName = t('Account name is required.');
    if (!form.accountNumber.trim()) next.accountNumber = t('Account number is required.');
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSubmit({
      bankName: form.bankName.trim(),
      accountName: form.accountName.trim(),
      accountNumber: form.accountNumber.trim(),
      label: form.label.trim() || null,
      branch: form.branch.trim() || null,
      currency: form.currency || null,
      swiftCode: form.swiftCode.trim() || null,
      iban: form.iban.trim() || null,
      isPrimary: form.isPrimary,
      notes: form.notes.trim() || null,
    });
  };

  return (
    <form className="sp-form" onSubmit={submit}>
      <div className="sp-form__row">
        <Input label={t('Bank name')} value={form.bankName} onChange={set('bankName')} placeholder="Bank Central Asia (BCA)" error={errors.bankName} autoFocus />
        <Input label={t('Branch')} value={form.branch} onChange={set('branch')} placeholder="KCU Denpasar" />
      </div>
      <div className="sp-form__row">
        <Input label={t('Account name')} value={form.accountName} onChange={set('accountName')} placeholder="PT Acme Foods" error={errors.accountName} />
        <Input label={t('Account number')} value={form.accountNumber} onChange={set('accountNumber')} placeholder="1234567890" error={errors.accountNumber} />
      </div>
      <div className="sp-form__row">
        <div className="ps-field">
          <span className="sp-field-label">{t('Currency')}</span>
          <Select value={form.currency} onChange={(v) => setForm((f) => ({ ...f, currency: v }))} options={currencyOptions} ariaLabel={t('Account currency')} />
        </div>
        <Input label={t('Label')} value={form.label} onChange={set('label')} placeholder={t('Main IDR account')} />
      </div>
      <div className="sp-form__row">
        <Input label="SWIFT / BIC" value={form.swiftCode} onChange={set('swiftCode')} placeholder="CENAIDJA" />
        <Input label="IBAN" value={form.iban} onChange={set('iban')} placeholder={t('Optional')} />
      </div>
      <Input label={t('Notes')} value={form.notes} onChange={set('notes')} placeholder={t('e.g. use for invoices over IDR 50M')} />
      <Switch checked={form.isPrimary} onChange={(v) => setForm((f) => ({ ...f, isPrimary: v }))} label={t('Primary account for payments')} />
      <div className="sp-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>{t('Cancel')}</Button>
        <Button type="submit" variant="primary" loading={submitting}>{t('Save bank account')}</Button>
      </div>
    </form>
  );
}

export default function BankAccountsTab({ supplier, onChanged }) {
  const toast = useToast();
  const t = useT();
  const accounts = supplier.bankAccounts || [];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (b) => { setEditing(b); setDrawerOpen(true); };
  const close = () => { if (!submitting) { setDrawerOpen(false); setEditing(null); } };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      const res = editing
        ? await suppliersApi.updateBankAccount(supplier.id, editing.id, payload)
        : await suppliersApi.addBankAccount(supplier.id, payload);
      onChanged(res);
      toast.success(editing ? t('Bank account updated') : t('Bank account added'));
      setDrawerOpen(false);
      setEditing(null);
    } catch (err) {
      toast.error(t('Could not save bank account'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await suppliersApi.removeBankAccount(supplier.id, deleteTarget.id);
      onChanged(res);
      toast.success(t('Bank account removed'));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(t('Could not remove bank account'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  const meta = (b) => [
    b.accountName,
    b.accountNumber,
    b.branch,
    b.swiftCode && `SWIFT ${b.swiftCode}`,
    b.iban && `IBAN ${b.iban}`,
  ].filter(Boolean).join(' · ');

  return (
    <div className="sp-panel">
      <div className="sp-section-head">
        <h2 className="sp-section-head__title">{t('Bank accounts')}</h2>
        <Button variant="primary" size="sm" leadingIcon={<Icon.Plus size={15} />} onClick={openCreate}>{t('Add bank account')}</Button>
      </div>

      {accounts.length === 0 ? (
        <EmptyState
          icon={<Icon.Landmark size={28} />}
          title={t('No bank accounts yet')}
          description={t("Add the supplier's bank details so payments can be remitted to the right account.")}
          action={<Button variant="primary" leadingIcon={<Icon.Plus size={15} />} onClick={openCreate}>{t('Add bank account')}</Button>}
        />
      ) : (
        <div className="sp-list">
          {accounts.map((b) => (
            <div key={b.id} className="sp-item">
              <div className="sp-item__main">
                <span className="sp-item__title">
                  <Icon.Landmark size={15} /> {b.label || b.bankName}
                  {b.label && <Badge tone="neutral">{b.bankName}</Badge>}
                  {b.currency && <Badge tone="neutral">{b.currency}</Badge>}
                  {b.isPrimary && <Badge tone="brand" dot>{t('Primary')}</Badge>}
                </span>
                <span className="sp-item__meta">{meta(b) || '—'}</span>
                {b.notes && <span className="sp-item__meta">{b.notes}</span>}
              </div>
              <div className="sp-item__actions">
                <IconButton aria-label={t('Edit bank account')} variant="ghost" size="icon-sm" onClick={() => openEdit(b)}>
                  <Icon.Pencil size={15} />
                </IconButton>
                <IconButton aria-label={t('Remove bank account')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(b)}>
                  <Icon.Trash size={15} />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer open={drawerOpen} onClose={close} title={editing ? t('Edit bank account') : t('Add bank account')} size="md">
        <BankAccountForm initial={editing} submitting={submitting} onSubmit={handleSubmit} onCancel={close} />
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
        onConfirm={handleDelete}
        title={t('Remove bank account')}
        description={deleteTarget ? t('Remove the account at {bank} ending in {digits}?', { bank: deleteTarget.bankName, digits: String(deleteTarget.accountNumber).slice(-4) }) : ''}
        confirmLabel={t('Remove')}
        tone="danger"
        loading={deleting}
      />
    </div>
  );
}
